import API, { ListOptions, QueryTypeOption } from "@services/root/rootApi";

import {
  AdvancedControllableProperty,
  ArchivedDevice,
  ArchivedDeviceFilters,
  BulkUpdateDeviceControlsPayload,
  Controls,
  DeviceComment,
  ManagementProtocol,
  ProxyDevice,
} from "./types";
import {
  Device,
  DevicesCount,
  DeviceFilters,
  DeviceCounters,
  MonitoredDeviceListOfDevice,
} from "./types";
import { deviceToProvisioning } from "./utils";
import { ListPage } from "@services/common/types";
import { Location } from "@services/locations/types";
import { Room } from "@services/rooms/types";
import {
  FlattenedManagementInterfaceKey,
  KeyofDevice,
} from "@common/utils/devices/validation";
import { FlattenedDevice } from "@pages/Assets/DeviceProvisioning/const";
import { Dispatch, SetStateAction } from "react";
import { KeyofRoom } from "@pages/Assets/RoomProvisioning/const";
import { KeyofLocation } from "@pages/Assets/LocationProvisioning/const";

export const getDeviceCounters = async (
  accountIds?: Array<string>
): Promise<DeviceCounters> => {
  const response = await API.post<DeviceCounters>(
    `/symphony-api/monitoring/device/counters`,
    accountIds
  );

  return response.data;
};

type GetDevicesOptions = QueryTypeOption &
  ListOptions & { includeExtendedProperties?: boolean };
export const getDevices = async (
  filters?: DeviceFilters,
  options?: GetDevicesOptions
): Promise<MonitoredDeviceListOfDevice> => {
  const {
    maxResults,
    orderBy,
    startIndex,
    includeExtendedProperties,
    queryType = "monitoring",
  } = options ?? {};
  const excludeSimpleAssets = Boolean(filters?.excludeSimpleAssets);
  delete filters?.excludeSimpleAssets;
  const inACall = filters?.inACall;
  if (filters && inACall !== undefined) {
    filters["status.inCall"] = inACall;
    delete filters.inACall;
  }
  const response = await API.post<MonitoredDeviceListOfDevice>(
    `/symphony-api/${queryType}/device`,
    {
      ...filters,
    },
    {
      params: {
        includeExtendedProperties,
        maxResults,
        orderBy,
        startIndex,
        excludeSimpleAssets,
      },
    }
  );

  return response.data;
};

export const getAggregatedDevices = async (
  id: Required<DeviceFilters>["aggregator.id"],
  provisionedOnly: boolean
) =>
  getDevices({
    ["aggregator.id"]: id,
    ["provisioned"]: provisionedOnly,
  });

export const getDevice = async (
  id: string,
  options?: QueryTypeOption & { includeRoomDetails?: boolean }
): Promise<Device> => {
  const { queryType = "monitoring", includeRoomDetails } = options ?? {};
  const response = await API.get(`/symphony-api/${queryType}/device/${id}`, {
    params: { includeRoomDetails },
  });
  return response.data;
};

export const getDevicesCount = async (
  filters?: DeviceFilters
): Promise<DevicesCount> => {
  const inACall = filters?.inACall;
  if (filters && inACall !== undefined) {
    filters["status.inCall"] = inACall;
    delete filters.inACall;
  }
  const response = await API.post<DevicesCount>(
    `/symphony-api/monitoring/device/count`,
    filters
  );

  return response.data;
};

export const deleteDevices = async (ids: Device["id"][], comment: string) => {
  await API.delete(`/symphony-api/provisioning/device`, {
    data: ids,
    params: { comment },
  });
};

export const updateDevice = async (id: Device["id"], device: Device) =>
  await API.put(`/symphony-api/provisioning/device/${id}`, {
    ...deviceToProvisioning(device),
  });

export const sendDeviceControl = async (
  id: string,
  controls: Controls
): Promise<void> => {
  await API.post(`/symphony-api/monitoring/device/${id}/control`, controls);
};

export const updateDeviceControl = async (
  id: string,
  control: AdvancedControllableProperty
): Promise<void> => {
  await API.post(
    `/symphony-api/monitoring/device/${id}/controllable/update`,
    control
  );
};

export const bulkUpdateDeviceControls = async (
  payload: BulkUpdateDeviceControlsPayload[]
): Promise<void> => {
  await API.post(
    `/symphony-api/monitoring/device/controllable/update`,
    payload
  );
};

export const updateDeviceComment = async (
  id: Device["id"],
  comment: DeviceComment
) =>
  await API.put(`/symphony-api/provisioning/device/${id}/notes`, {
    ...comment,
  });

export const getProxyDevices = async (
  filters: ProxyDevice
): Promise<ListPage<Device>> => {
  const { roomLocationId, ...others } = filters;
  const response = await API.post(`/symphony-api/provisioning/device`, {
    "room.location.id": roomLocationId,
    ...others,
  });
  return response.data;
};

export const putDevice = async (device: Device) =>
  await API.put(
    `/symphony-api/provisioning/device/${
      device.id !== undefined ? `${device.id}` : ""
    }`,
    device
  );

export type BackendDeviceField = `device.${KeyofDevice}`;
export const trimBackendDeviceField = (
  field: BackendDeviceField
): KeyofDevice => {
  let trimed = field.replace(/^device\./g, "") as KeyofDevice;

  // Handles backend sending back the property in the form property.protocol
  // instead of protocol.property
  const [details, managementInterfaces, property, protocol] = trimed.split(".");
  const lowerCaseToEnum = {
    http: ManagementProtocol.HTTP,
    telnet: ManagementProtocol.TELNET,
    ssh: ManagementProtocol.SSH,
  };
  if (
    trimed
      .toLocaleLowerCase()
      .match(/^details\.managementinterfaces\.([^.]*)\.(http|ssh|telnet)/g) !==
    null
  ) {
    trimed = `${details}.${managementInterfaces}.${
      lowerCaseToEnum[protocol.toLowerCase() as keyof typeof lowerCaseToEnum]
    }.${property}` as FlattenedManagementInterfaceKey;
  }
  if (
    trimed.toLocaleLowerCase().match(/^details\.managementinterfaces/g) !== null
  ) {
    trimed =
      `${details}.${managementInterfaces}.${lowerCaseToEnum["http"]}.localPort` as FlattenedManagementInterfaceKey;
  }

  return trimed;
};
export const trimBackendRoomField = (field: BackendDeviceField): KeyofRoom =>
  field.replace(/^room\./g, "") as KeyofRoom;
export const trimBackendLocationField = (
  field: BackendDeviceField
): KeyofLocation => field.replace(/^location\./g, "") as KeyofLocation;
export type BackendDeviceValidation = {
  deviceId: string;
  // Will only contain one field as per the comment in ProvisioningServiceImpl.validateAndCollectErrorsDrpDevice
  // On the backend side
  fields: BackendDeviceField[];
  exceptions: string[];
};

export type BackendUpdateDeviceValidation = {
  error: string;
  errorCode?: string;
  errorContext: ErrorContext;
  message: string;
};
type ErrorContext = {
  constraint: string;
  entity: string;
  entityId: string;
  invalidArgument: string;
};

export const isUUID = (id: string) => {
  return id.length === 36 ? true : false;
};
export type BackendConvertSheet = {
  name: string;
  rows: string[][];
};
export type BackendConvertSheets = BackendConvertSheet[];
export const roomsToDRPLocations = (rooms: Room[]) => {
  const locations = rooms.reduce((acc, room) => {
    if (room?.location === undefined) return acc;
    const currentLocation: (typeof acc)[number] = {
      ...room?.location,
      id: room?.location?.id ?? "",
      rooms: [],
      name: room?.location?.name ?? "",
    };
    let foundLocation;
    if (currentLocation.id)
      foundLocation = acc.find((l) => l.id === currentLocation.id);
    else foundLocation = acc.find((l) => l.name === currentLocation.name);
    if (foundLocation === undefined) {
      acc.push(currentLocation);
      foundLocation = currentLocation;
    }
    let currentRoom = { ...room };
    delete currentRoom.location;
    let foundRoom;
    if (currentRoom.id)
      foundRoom = foundLocation.rooms.find((r) => r.id === currentRoom?.id);
    else
      foundRoom = foundLocation.rooms.find((r) => r.name === currentRoom?.name);
    if (foundRoom === undefined) {
      foundLocation.rooms.push(currentRoom);
      foundRoom = currentRoom;
    }
    return acc;
  }, [] as (Pick<Required<Location>, "id"> & Pick<Location, "name"> & { rooms: Room[] })[]);
  return locations;
};
export const devicesToDRPLocations = (devices: Device[]) => {
  const locations = devices.reduce((acc, device) => {
    if (device?.room?.location === undefined) return acc;
    const currentLocation: (typeof acc)[number] = {
      ...device?.room?.location,
      id: device?.room?.location?.id ?? "",
      rooms: [],
      name: device?.room?.location?.name ?? "",
    };
    if (currentLocation === undefined) return acc;
    let foundLocation;
    if (currentLocation.id)
      foundLocation = acc.find((l) => l.id === currentLocation.id);
    else foundLocation = acc.find((l) => l.name === currentLocation.name);
    if (foundLocation === undefined) {
      acc.push(currentLocation);
      foundLocation = currentLocation;
    }
    let currentRoom = { ...device?.room };
    delete currentRoom.location;
    let foundRoom;
    if (currentRoom.id)
      foundRoom = foundLocation.rooms.find((r) => r.id === currentRoom?.id);
    else
      foundRoom = foundLocation.rooms.find((r) => r.name === currentRoom?.name);
    if (foundRoom === undefined) {
      foundLocation.rooms.push(currentRoom);
      foundRoom = currentRoom;
    }
    let currentDevice = { ...device };
    delete currentDevice.room;
    if (foundRoom.devices === undefined) foundRoom.devices = [];
    currentDevice.details = currentDevice.details ?? {};
    foundRoom.devices.push(currentDevice);

    return acc;
  }, [] as (Pick<Required<Location>, "id"> & Pick<Location, "name"> & { rooms: Room[] })[]);
  return locations;
};

export const toDRPLocations = (
  data: Device[] | Room[] | Location[],
  importLevel: "devices" | "rooms" | "locations" = "devices"
) => {
  switch (importLevel) {
    case "devices":
      return devicesToDRPLocations(data as Device[]);
    case "rooms":
      return roomsToDRPLocations(data as Room[]);
    case "locations":
      return data;
  }
};
export const validateDevices = async (
  devices: Device[],
  importLevel: "devices" | "rooms" | "locations" = "devices"
) => {
  return (
    await API.put<BackendDeviceValidation[]>(
      `/symphony-api/provisioning/device/validate`,
      toDRPLocations(devices, importLevel)
    )
  ).data;
};

export type DRPResult = {
  locations?: number;
  rooms?: number;
  devices?: number;
};
export const importDevices = async (
  devices: Device[],
  importLevel: "devices" | "rooms" | "locations" = "devices"
) => {
  return (
    await API.put<DRPResult>(
      `/symphony-api/provisioning/device/import`,
      toDRPLocations(devices, importLevel)
    )
  ).data;
};

export const getDevicesCommonProperties = async (id: Array<Device["id"]>) =>
  (
    await API.post<Partial<FlattenedDevice>>(
      "symphony-api/provisioning/device/commonproperties",
      { id }
    )
  ).data;

export const setDevicesCommonProperties = async (
  id: Array<Device["id"]>,
  field: Partial<FlattenedDevice>
) =>
  (
    await API.put<Partial<FlattenedDevice>>(
      "symphony-api/provisioning/device/commonproperties",
      { id, field }
    )
  ).data;

export const setDevicesProperties = async (
  devices: Array<
    Partial<FlattenedDevice> & Required<Pick<FlattenedDevice, "id">>
  >
) =>
  await API.put<never>("symphony-api/provisioning/device/properties", devices);

export const fileConvert = async (
  data: FormData,
  setProgress: Dispatch<SetStateAction<number>>
) => {
  return (
    await API.post<BackendConvertSheets>(
      `/symphony-api/provisioning/device/file/convert`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = (progressEvent.progress ?? 0) * 100;
          setProgress(progress);
        },
      }
    )
  ).data;
};

export const getArchivedDevices = async (
  filters?: ArchivedDeviceFilters,
  options?: ListOptions
): Promise<ListPage<ArchivedDevice>> => {
  const { maxResults, orderBy, startIndex } = options ?? {};
  return (
    await API.post(
      `/symphony-api/provisioning/device/archived`,
      {
        ...filters,
      },
      {
        params: {
          orderBy: orderBy ?? "name",
          maxResults: maxResults,
          startIndex: startIndex ?? 0,
        },
      }
    )
  ).data;
};
